import React, {createContext, useContext, useEffect, useState} from 'react'

export const brandThemeIds = ['indigo', 'celeste', 'violeta'] as const

export type BrandThemeType = typeof brandThemeIds[number]
export const brandThemeLSKey = 'osoec_brand_theme'
export const defaultBrandTheme: BrandThemeType = 'indigo'

type BrandThemeContextType = {
  brand: BrandThemeType
  updateBrand: (_brand: BrandThemeType) => void
}

const isValidBrandTheme = (value: string | null): value is BrandThemeType =>
  !!value && (brandThemeIds as readonly string[]).includes(value)

const getBrandThemeFromLocalStorage = (): BrandThemeType => {
  if (!localStorage) {
    return defaultBrandTheme
  }

  const data = localStorage.getItem(brandThemeLSKey)
  if (isValidBrandTheme(data)) {
    return data
  }

  return defaultBrandTheme
}

const BrandThemeContext = createContext<BrandThemeContextType>({
  brand: getBrandThemeFromLocalStorage(),
  updateBrand: (_brand: BrandThemeType) => {},
})

const useBrandTheme = () => useContext(BrandThemeContext)

const BrandThemeProvider = ({children}: {children: React.ReactNode}) => {
  const [brand, setBrand] = useState<BrandThemeType>(getBrandThemeFromLocalStorage())

  const updateBrand = (_brand: BrandThemeType, saveInLocalStorage: boolean = true) => {
    setBrand(_brand)
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(brandThemeLSKey, _brand)
    }
    document.documentElement.setAttribute('data-brand-theme', _brand)
  }

  useEffect(() => {
    updateBrand(brand, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <BrandThemeContext.Provider value={{brand, updateBrand}}>{children}</BrandThemeContext.Provider>
  )
}

export {BrandThemeProvider, useBrandTheme}
